/**
 * useSelectionSync - Sync Selections with API Data
 *
 * Single responsibility: Loading existing selections from API response.
 * Syncs local state with server data when pickem details are loaded.
 */

import { useEffect, useRef } from 'react';
import type { PickemDetailResponse } from '@bet99-pickem/api-types';
import { QUESTION_TYPES } from '@/constants/question-types';
import { getQuestionId, getOptionId, toId } from '@/utils/id-helpers';
import type { SelectionState } from './useSelectionState';

/**
 * Syncs selection state with API data
 *
 * @param pickemDetails - The pickem details from API
 * @param setSelections - Function to update selections state
 */
export function useSelectionSync(
  pickemDetails: PickemDetailResponse | undefined,
  setSelections: React.Dispatch<React.SetStateAction<SelectionState>>
) {
  // Track whether we've already synced to prevent overwriting manual changes
  const hasSynced = useRef(false);

  useEffect(() => {
    // Only sync once when data is first loaded
    if (!pickemDetails?.pickem.questions || hasSynced.current) return;

    const existingTeams: Record<string, string> = {};
    const existingProps: Record<string, 'yes' | 'no'> = {};
    let existingTieBreaker = '';

    // Load team and prop selections from questions
    pickemDetails.pickem.questions.forEach(question => {
      if (!question.user_selection) return;

      const questionId = getQuestionId(question);

      // Handle team and player questions
      if (
        question.question_type === QUESTION_TYPES.TEAM ||
        question.question_type === QUESTION_TYPES.PLAYER
      ) {
        existingTeams[questionId] = toId(question.user_selection);
      }
      // Handle prop questions
      else if (question.question_type === QUESTION_TYPES.PROP) {
        // Convert both IDs to strings for comparison to handle string/number mismatches
        const userSelectionId = toId(question.user_selection);
        const selectedOption = question.options.find(opt => getOptionId(opt) === userSelectionId);

        if (selectedOption) {
          // First try to detect by title
          const optTitle = selectedOption.title_en?.toLowerCase();
          if (optTitle?.includes('yes')) {
            existingProps[questionId] = 'yes';
          } else if (optTitle?.includes('no')) {
            existingProps[questionId] = 'no';
          } else {
            // Fall back to position: first option = yes, second = no
            const optionIndex = question.options.findIndex(
              opt => getOptionId(opt) === userSelectionId
            );
            existingProps[questionId] = optionIndex === 0 ? 'yes' : 'no';
          }
        }
      }
    });

    // Load tiebreaker custom_value from selections array
    if (pickemDetails?.pickem.selections && Array.isArray(pickemDetails.pickem.selections)) {
      const tiebreakerQuestion = pickemDetails.pickem.questions.find(
        q => q.question_type === QUESTION_TYPES.TIEBREAKER
      );

      if (tiebreakerQuestion) {
        const tiebreakerQuestionId = getQuestionId(tiebreakerQuestion);
        const tiebreakerSelection = pickemDetails.pickem.selections.find(
          s =>
            s.question_id === tiebreakerQuestionId || String(s.question_id) === tiebreakerQuestionId
        );

        if (
          tiebreakerSelection?.custom_value !== null &&
          tiebreakerSelection?.custom_value !== undefined
        ) {
          existingTieBreaker = String(tiebreakerSelection.custom_value);
        }
      }
    }

    // Update state with loaded selections
    setSelections(prev => ({
      ...prev,
      teams: existingTeams,
      props: existingProps,
      tieBreaker: existingTieBreaker,
    }));

    // Mark that we've synced to prevent overwriting manual changes
    hasSynced.current = true;
  }, [pickemDetails, setSelections]);
}
